import { useMutation } from '@ngtrvu/use-api';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchApiGet } from 'hooks/api';
import { Form, message } from 'antd';
import PageTitle from 'apps/home/components/PageTitle';
import ErrorMessage from 'common/ui/ErrorMessage';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { convertFormData, normalizeData } from 'utils/formData.js';

const AdminEdit = ({
  ItemForm,
  listUrl,
  apiCall,
  resourceName,
  title,
  detailUrl,
}) => {
  const [form] = Form.useForm();
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: item, isLoading, error: fetchItemError, load } = useFetchApiGet(
    apiCall.detail,
    { resourceName: resourceName || 'data' }
  );

  useEffect(() => {
    load(params);
  }, [JSON.stringify(params)]);

  const {
    mutate: submitItem,
    isPending: isSubmitting,
    error: submitItemError,
  } = useMutation(apiCall.edit, {
    onSuccess: () => {
      // Invalidate the detail query to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: [apiCall.detail.queryKey],
      });
      // Also invalidate the list query if it exists
      if (apiCall.list) {
        queryClient.invalidateQueries({
          queryKey: [apiCall.list.queryKey],
        });
      }
      message.success(`${title} updated successfully`);
      navigate(-1);
    },
  });

  const {
    mutate: deleteItem,
    isPending: isDeleting,
    error: deleteItemError,
  } = useMutation(apiCall.delete || (() => {}), {
    onSuccess: () => {
      if (apiCall.list) {
        queryClient.invalidateQueries({
          queryKey: [apiCall.list.queryKey],
        });
      }
      listUrl ? navigate(listUrl) : navigate(-1);
    },
    onError: error => {
      console.error(error);
    },
  });

  const onFinish = (values, photoFields = ['photo']) => {
    const postData = async () => {
      try {
        let submitData = normalizeData(values);
        let isConvertFormData = false;

        for (let i = 0; i < photoFields.length; i++) {
          const field = photoFields[i];
          const fieldValue = values[field];
          console.log('fieldValue', fieldValue);
          if (
            fieldValue &&
            fieldValue.at(-1) &&
            fieldValue.at(-1).originFileObj instanceof File
          ) {
            submitData[field] = values[field].at(-1).originFileObj;
            isConvertFormData = true;
          } else {
            delete submitData[field];
          }
        }
        if (isConvertFormData) {
          submitData = convertFormData(submitData);
        }
        submitItem({ ...params, payload: submitData });
      } catch (error) {
        console.error(error.message);
      }
    };

    postData();
  };

  if (isLoading || (!item && !fetchItemError)) {
    return <LoadingSpinner />;
  }

  if (fetchItemError) {
    return <ErrorMessage {...fetchItemError} />;
  }

  return (
    <>
      <PageTitle title={`${title || 'Edit Item'} `} backUrl={listUrl} />
      <ItemForm
        params={params}
        submiting={isSubmitting}
        submitItemError={submitItemError}
        deleting={isDeleting}
        deleteItemError={deleteItemError}
        form={form}
        onFinish={onFinish}
        initialValues={item}
        name="edit_form"
        visibility="visible"
        onDelete={
          apiCall.delete
            ? () => {
                if (confirm('Are you sure?')) {
                  deleteItem(params);
                }
              }
            : undefined
        }
      />
    </>
  );
};

export default AdminEdit;
